import { useEffect, useMemo, useRef, useState } from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import MenuItem from '@mui/material/MenuItem';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Link as LinkDeRota, useNavigate, useSearchParams } from 'react-router-dom';

import { ErroDaApi } from '../../shared/api/erros';
import { EstadoDeCarregamento } from '../../shared/ui/EstadoDeCarregamento';
import { EstadoDeErro } from '../../shared/ui/EstadoDeErro';
import { EstadoVazio } from '../../shared/ui/EstadoVazio';
import { caminhoDaParametrizacao } from '../../shared/navegacao/mapaDeNavegacao';
import { chavesDeContratos, opcoesDasFontes, opcoesDosContratos } from './api/contratos';
import { chavesDoMapeamento } from './api/mapeamento';
import { CONFERENCIAS_DO_ITEM, nomeDoContratoComUf, type ConferenciaDoItem, type Contrato } from './api/tipos';
import { ABAS_DO_CONTRATO, lerAbaDoContrato, type AbaDoContrato } from './componentes/abasDoContrato';
import { CadastroDeContrato } from './componentes/CadastroDeContrato';
import { ClausulasDoContrato } from './componentes/ClausulasDoContrato';
import { ComparacaoDeContratos } from './componentes/ComparacaoDeContratos';
import { EnvioDeFonte } from './componentes/EnvioDeFonte';
import { ItensDoContrato } from './componentes/ItensDoContrato';
import { ListaDeFontes } from './componentes/ListaDeFontes';
import { RegrasDoContrato } from './componentes/RegrasDoContrato';
import { EnvioDeReajuste } from './reajuste/EnvioDeReajuste';
import { EnvioDeEdicao } from './tabelaExterna/EnvioDeEdicao';
import { competenciaDe } from '../../shared/dominio/datas';

/**
 * O reajuste fica escondido: o bloco aplica UM percentual a todos os itens, e os reajustes reais
 * que temos não cabem nisso (Unimed Guarulhos: 17% em serviços e 4,68% em materiais; Cassi e Amil:
 * tabela de preços nova). Só aparece com `VITE_REAJUSTE_HABILITADO=true`; some de vez da chave
 * quando houver percentual por grupo ou leitura do aditivo de preço (ver `notes/concept-services/todo.md`).
 */
const REAJUSTE_HABILITADO = import.meta.env.VITE_REAJUSTE_HABILITADO === 'true';

/**
 * A tabela externa (SIMPRO e Brasíndice) também fica escondida: só rodou com uma edição de teste
 * montada à mão, nunca com uma edição real do catálogo, e o formato do arquivo do cliente não foi
 * confirmado. Religa com `VITE_TABELA_EXTERNA_HABILITADA=true`.
 */
const TABELA_EXTERNA_HABILITADA = import.meta.env.VITE_TABELA_EXTERNA_HABILITADA === 'true';

/** O que ainda não está ligado, numa linha no fim da aba Fontes, em vez de três blocos vazios (`TP-067`, `D3`). */
const EM_BREVE = [
  REAJUSTE_HABILITADO ? null : 'reajuste',
  TABELA_EXTERNA_HABILITADA ? null : 'tabela externa (SIMPRO e Brasíndice)',
].filter((parte): parte is string => parte !== null);

function lerConferencia(valor: string | null): ConferenciaDoItem | '' {
  return (CONFERENCIAS_DO_ITEM as readonly string[]).includes(valor ?? '') ? (valor as ConferenciaDoItem) : '';
}

/**
 * Contratos (`TP-049`, T9; `TP-067`): o contrato pelo que foi mapeado dele. Sob o contrato e a
 * competência escolhidos, quatro abas: as Fontes (envio do PDF e a situação de cada leitura, a aba inicial),
 * as Regras vigentes por tipo, as Cláusulas e os Itens. A aba vai para a URL (`?aba=`). A
 * comparação fica fora das abas: compara dois contratos quaisquer, não só o escolhido.
 */
export function PaginaDeContratos(): React.JSX.Element {
  const navegar = useNavigate();
  const consultaDosContratos = useQuery(opcoesDosContratos());
  const [competencia, definirCompetencia] = useState(competenciaDe());
  const [parametros, definirParametros] = useSearchParams();
  const aba = lerAbaDoContrato(parametros.get('aba'));
  const conferenciaDaUrl = lerConferencia(parametros.get('conferencia'));
  // O contrato escolhido vai para a URL (`?contrato=`), como a aba: recarregar ou voltar de outra tela não
  // pode cair no primeiro contrato da lista. Sem o parâmetro, o PRIMEIRO contrato é o efetivo — derivado
  // no render, não num `useEffect` (`react-hooks/set-state-in-effect`).
  const contratoEscolhidoId = parametros.get('contrato') ?? '';
  const definirContratoEscolhidoId = (id: string): void => {
    definirParametros(
      (atuais) => {
        const proximos = new URLSearchParams(atuais);
        proximos.set('contrato', id);
        proximos.delete('conferencia');
        return proximos;
      },
      { replace: true },
    );
  };

  const abrir = (nova: AbaDoContrato, conferencia?: ConferenciaDoItem): void => {
    definirParametros(
      (atuais) => {
        const proximos = new URLSearchParams(atuais);
        if (nova === 'fontes') proximos.delete('aba');
        else proximos.set('aba', nova);
        if (conferencia !== undefined) proximos.set('conferencia', conferencia);
        else proximos.delete('conferencia');
        return proximos;
      },
      { replace: true },
    );
  };

  const contratos = useMemo(() => consultaDosContratos.data ?? [], [consultaDosContratos.data]);
  const contratoId = contratos.some((c) => c.id === contratoEscolhidoId)
    ? contratoEscolhidoId
    : (contratos[0]?.id ?? '');

  useEffect(() => {
    if (consultaDosContratos.error instanceof ErroDaApi && consultaDosContratos.error.status === 401) {
      void navegar('/entrar', { replace: true });
    }
  }, [consultaDosContratos.error, navegar]);

  const consultaDasFontes = useQuery({ ...opcoesDasFontes(contratoId), enabled: contratoId !== '' });

  // Quando o estado de uma fonte muda (a lista se reconsulta sozinha enquanto há leitura), o que foi mapeado
  // também mudou: recarrega resumo, regras, cláusulas, itens e detalhes, sem a pessoa precisar dar F5.
  const clienteDeConsultas = useQueryClient();
  const estadosDasFontes = (consultaDasFontes.data ?? []).map((f) => `${f.id}:${f.estado}`).join('|');
  const estadosAnteriores = useRef<string | null>(null);
  useEffect(() => {
    if (estadosDasFontes === '') return;
    if (estadosAnteriores.current !== null && estadosAnteriores.current !== estadosDasFontes) {
      void clienteDeConsultas.invalidateQueries({ queryKey: chavesDoMapeamento.raiz });
      void clienteDeConsultas.invalidateQueries({ queryKey: [...chavesDeContratos.raiz, contratoId] });
      void clienteDeConsultas.invalidateQueries({ queryKey: [...chavesDeContratos.raiz, 'fonte'] });
    }
    estadosAnteriores.current = estadosDasFontes;
  }, [estadosDasFontes, clienteDeConsultas, contratoId]);
  const escolherCadastrado = (contrato: Contrato): void => {
    definirContratoEscolhidoId(contrato.id);
  };

  return (
    <Stack spacing={2}>
      <Box>
        <Typography variant="h1">Contratos</Typography>
        <Typography variant="body2" color="text.secondary">
          O que foi lido de cada contrato: as regras, as cláusulas de onde saíram e os itens da tabela de preço. O PDF
          do contrato entra pela aba Fontes.
        </Typography>
      </Box>

      {consultaDosContratos.isPending ? <EstadoDeCarregamento linhas={4} /> : null}

      {consultaDosContratos.isError ? (
        <EstadoDeErro
          erro={consultaDosContratos.error}
          contexto="Não foi possível carregar os contratos"
          aoReprocessar={() => {
            void consultaDosContratos.refetch();
          }}
        />
      ) : null}

      {consultaDosContratos.isSuccess && contratos.length === 0 ? (
        <>
          <EstadoVazio
            titulo="Nenhum contrato cadastrado"
            descricao="Cadastre o primeiro contrato (operadora, entidade, UF) para enviar o PDF."
          />
          <CadastroDeContrato aoCadastrar={escolherCadastrado} />
        </>
      ) : null}

      {consultaDosContratos.isSuccess && contratos.length > 0 ? (
        <>
          <CadastroDeContrato aoCadastrar={escolherCadastrado} />

          <Stack direction="row" spacing={1.5} useFlexGap sx={{ flexWrap: 'wrap' }}>
            <TextField
              select
              label="Contrato"
              size="small"
              value={contratoId}
              onChange={(evento) => {
                definirContratoEscolhidoId(evento.target.value);
              }}
              sx={{ width: 360, maxWidth: '100%' }}
            >
              {contratos.map((contrato) => (
                <MenuItem key={contrato.id} value={contrato.id}>
                  {nomeDoContratoComUf(contrato)}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              label="Competência"
              type="month"
              size="small"
              value={competencia}
              onChange={(evento) => {
                definirCompetencia(evento.target.value);
              }}
              slotProps={{ inputLabel: { shrink: true } }}
            />
          </Stack>

          {contratoId !== '' ? (
            <Paper variant="outlined">
              <Box sx={{ borderBottom: 1, borderColor: 'divider', px: 1 }}>
                <Tabs
                  value={aba}
                  onChange={(_evento, nova: AbaDoContrato) => {
                    abrir(nova);
                  }}
                  aria-label="Partes do contrato"
                  variant="scrollable"
                  allowScrollButtonsMobile
                >
                  {ABAS_DO_CONTRATO.map((a) => (
                    <Tab
                      key={a.chave}
                      value={a.chave}
                      id={`aba-${a.chave}`}
                      aria-controls={`painel-${a.chave}`}
                      label={a.rotulo}
                    />
                  ))}
                </Tabs>
              </Box>

              <Box role="tabpanel" id={`painel-${aba}`} aria-labelledby={`aba-${aba}`} sx={{ p: 2 }}>
                {aba === 'regras' ? (
                  <RegrasDoContrato key={contratoId} contratoId={contratoId} competencia={competencia} abrir={abrir} />
                ) : null}

                {aba === 'clausulas' ? (
                  <ClausulasDoContrato key={contratoId} contratoId={contratoId} competencia={competencia} />
                ) : null}

                {aba === 'itens' ? (
                  <ItensDoContrato
                    key={`${contratoId}-${conferenciaDaUrl}`}
                    contratoId={contratoId}
                    competencia={competencia}
                    conferenciaInicial={conferenciaDaUrl}
                  />
                ) : null}

                {aba === 'fontes' ? (
                  <Stack spacing={3}>
                    <Box>
                      <Typography variant="h2" sx={{ mb: 1 }}>
                        Fontes deste contrato
                      </Typography>
                      <ListaDeFontes consulta={consultaDasFontes} />
                    </Box>

                    <EnvioDeFonte contratoId={contratoId} />

                    {REAJUSTE_HABILITADO ? <EnvioDeReajuste contratoId={contratoId} /> : null}

                    <Box>
                      <Typography variant="h2" sx={{ mb: 1 }}>
                        Contrato × IW
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                        Preço, código e vínculo de cada item deste contrato contra a tabela do IW.
                      </Typography>
                      {contratos.find((c) => c.id === contratoId)?.ligadoAoIw ? (
                        <Button variant="outlined" component={LinkDeRota} to={caminhoDaParametrizacao(contratoId)}>
                          Abrir comparação com o IW
                        </Button>
                      ) : (
                        <Typography variant="body2">Este contrato não está ligado a um contrato do IW.</Typography>
                      )}
                    </Box>

                    {TABELA_EXTERNA_HABILITADA ? <EnvioDeEdicao /> : null}

                    {EM_BREVE.length > 0 ? (
                      <Typography variant="caption" color="text.secondary">
                        Em breve: {EM_BREVE.join(', ')}.
                      </Typography>
                    ) : null}
                  </Stack>
                ) : null}
              </Box>
            </Paper>
          ) : null}

          <Paper variant="outlined" sx={{ p: 2 }}>
            <ComparacaoDeContratos contratos={contratos} />
          </Paper>
        </>
      ) : null}
    </Stack>
  );
}
